import { useState } from 'react'
import firebase from '../../firebase/compat'
import { ADDRESS_STEP_TEXTS } from './linkStepTexts'
import { isDutchPostcode } from '../../helpers/pdok/verifyAddressMatch'

// De adresstap vóór een openbare vragenlijst (Research.AskAddressFirst).
//
// De browser weet niet bij welk huishouden hij hoort en hoort dat ook niet te
// weten: matchQuestionnaireAddress zoekt het adres op, beslist, en zet de
// koppeling klaar voor saveQuestionnaireResponse. Wat hier terugkomt is alleen
// wat PDOK zegt (meerdere woningen, adres onbekend) — dat is openbaar.
//
// Valt dicht: de vragen verschijnen pas na een geslaagde aanroep. Een fout
// zegt 'probeer opnieuw' en laat de invuller niet stil zonder koppeling door.
// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading

// Zelfde naam als het lokveld van het intakeformulier; de server weigert een
// gevulde waarde vóór elke read.
const HONEYPOT_KEY = 'website'

/**
 * @typedef {'invalid'|'ambiguous'|'not-found'|'error'|''} StepProblem
 */

/**
 * @param {object} props
 * @param {string} props.client - CompagnyID uit de url
 * @param {string} props.research - ResearchID uit de url
 * @param {string} props.moment - MomentID uit de url
 * @param {string} props.sessionId - De invulsessie
 * @param {Record<string, string>} props.translations - Vertalingen van de vragenlijst; Nederlands als terugval
 * @param {() => void} props.onDone - Aangeroepen zodra de koppeling is opgeslagen
 */
const AddressStep = ({ client, research, moment, sessionId, translations, onDone }) => {
    const [postcode, setPostcode] = useState('')
    const [huisnummer, setHuisnummer] = useState('')
    const [toevoeging, setToevoeging] = useState('')
    const [honeypot, setHoneypot] = useState('')
    const [saving, setSaving] = useState(false)
    const [problem, setProblem] = useState(/** @type {StepProblem} */ (''))
    const [options, setOptions] = useState(/** @type {string[]} */ ([]))

    /** @param {string} key */
    const t = (key) => translations?.[key] || ADDRESS_STEP_TEXTS[key]

    const send = async ({ chosenToevoeging = toevoeging, confirmNotFound = false } = {}) => {
        if (saving) return
        if (!isDutchPostcode(postcode) || !/^\d/.test(huisnummer.trim())) {
            setProblem('invalid')
            return
        }

        setSaving(true)
        setProblem('')
        try {
            const call = firebase.app().functions('europe-west1').httpsCallable('matchQuestionnaireAddress')
            const { data } = await call({
                client,
                researchId: research,
                momentId: moment,
                sessionId,
                postcode,
                huisnummer,
                toevoeging: chosenToevoeging,
                [HONEYPOT_KEY]: honeypot,
                ...(confirmNotFound ? { confirmNotFound: true } : {}),
            })

            if (data?.ok) {
                onDone()
                return
            }
            if (data?.reason === 'ambiguous') {
                setOptions(Array.isArray(data.options) ? data.options : [])
                setProblem('ambiguous')
                return
            }
            if (data?.reason === 'not-found') {
                setProblem('not-found')
                return
            }
            setProblem('error')
        } catch (error) {
            // Zichtbaar laten, niet doorlaten: zonder koppeling komen de
            // antwoorden bij niemand terecht. AI-CONTEXT: docs/pitfalls.md#error-catch-all
            console.error('Adresstap: opslaan mislukt', error)
            setProblem('error')
        } finally {
            setSaving(false)
        }
    }

    const chooseToevoeging = (option) => {
        setToevoeging(option)
        send({ chosenToevoeging: option })
    }

    const submitHandler = (e) => {
        e.preventDefault()
        send()
    }

    const messages = {
        invalid: t('Adresstap ongeldig'),
        ambiguous: t('Adresstap meerdere woningen'),
        'not-found': t('Adresstap niet gevonden'),
        error: t('Adresstap fout'),
    }

    return (
        <form className="address-step" onSubmit={submitHandler} noValidate>
            <h2>{t('Adresstap titel')}</h2>
            <p>
                {t('Adresstap uitleg')}
            </p>

            <label className="address-step-field">
                <span>{t('Postcode')}</span>
                <input type="text" autoComplete="postal-code" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
            </label>
            <label className="address-step-field">
                <span>{t('Huisnummer')}</span>
                <input type="text" inputMode="numeric" value={huisnummer} onChange={(e) => setHuisnummer(e.target.value)} />
            </label>
            <label className="address-step-field">
                <span>{t('Toevoeging (optioneel)')}</span>
                <input type="text" value={toevoeging} onChange={(e) => setToevoeging(e.target.value)} />
            </label>

            {/* Lokveld: buiten beeld, niet in de tabvolgorde, niet voor schermlezers. */}
            <input
                className="intake-honeypot"
                type="text"
                name={HONEYPOT_KEY}
                data-testid="address-step-honeypot"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
            />

            {problem && <p className="address-step-problem" role="alert">{messages[problem]}</p>}

            {problem === 'ambiguous' && options.length > 0 && (
                <div className="address-step-options">
                    {options.map((option) => (
                        <button key={option || 'zonder'} type="button" disabled={saving} onClick={() => chooseToevoeging(option)}>
                            {option || t('Zonder toevoeging')}
                        </button>
                    ))}
                </div>
            )}

            <div className="questionnaire-button-container">
                {problem === 'not-found' && (
                    <button type="button" disabled={saving} onClick={() => send({ confirmNotFound: true })}>
                        {t('Adresstap klopt')}
                    </button>
                )}
                <button type="submit" disabled={saving}>
                    {saving ? t('Bezig') : t('Verder')}
                </button>
            </div>
        </form>
    )
}

export default AddressStep
